// src/pages/admin/AdminExtraBreaks.jsx
import React, { useState, useMemo } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useExtraBreaks } from "../../hooks/useFirestoreExtensions";
import { useAllUsers } from "../../hooks/useFirestore";
import { ADMIN_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";

export default function AdminExtraBreaks() {
  const { data: rawBreaks, loading: breaksLoading } = useExtraBreaks();
  const { data: users, loading: usersLoading } = useAllUsers();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filterLead, setFilterLead] = useState("all");

  const depts = useMemo(() => {
    return [...new Set(users.map((u) => u.department))].filter(Boolean).sort();
  }, [users]);

  const leads = useMemo(() => {
    return users.filter((u) => u.role === "lead").sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [users]);

  const leadMap = useMemo(() => {
    const map = {};
    users.forEach((u) => {
      map[u.uid] = u.displayName;
    });
    return map;
  }, [users]);

  const processedBreaks = useMemo(() => {
    return rawBreaks.map((b) => {
      const leadName = b.teamId ? (leadMap[b.teamId] || "Unknown Lead") : "No Lead";
      return {
        ...b,
        leadName,
      };
    });
  }, [rawBreaks, leadMap]);

  const filteredBreaks = useMemo(() => {
    return processedBreaks.filter((b) => {
      // Search query
      const matchSearch =
        searchQuery === "" ||
        (b.employeeName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (b.employeeId || "").toLowerCase().includes(searchQuery.toLowerCase());

      // Department
      const matchDept = filterDept === "all" || b.department === filterDept;

      // Break type
      const matchType = filterType === "all" || b.breakType === filterType;

      // Team Lead
      const matchLead = filterLead === "all" || b.teamId === filterLead;

      // Dates
      let matchDate = true;
      if (b.date) {
        if (startDate && b.date < startDate) matchDate = false;
        if (endDate && b.date > endDate) matchDate = false;
      }

      return matchSearch && matchDept && matchType && matchLead && matchDate;
    });
  }, [processedBreaks, searchQuery, filterDept, filterType, filterLead, startDate, endDate]);

  // Statistics
  const stats = useMemo(() => {
    const totalIncidents = filteredBreaks.length;
    const totalMins = filteredBreaks.reduce((acc, curr) => acc + (curr.extraMins || 0), 0);
    const avgMins = totalIncidents > 0 ? Math.round((totalMins / totalIncidents) * 10) / 10 : 0;

    // Violator frequency
    const freq = {};
    filteredBreaks.forEach((b) => {
      const name = b.employeeName || "Unknown";
      freq[name] = (freq[name] || 0) + 1;
    });
    let topViolator = "N/A";
    let topCount = 0;
    Object.entries(freq).forEach(([name, count]) => {
      if (count > topCount) {
        topViolator = name;
        topCount = count;
      }
    });

    return { totalIncidents, totalMins, avgMins, topViolator, topCount };
  }, [filteredBreaks]);

  const formatTime = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  };

  const handleExportExcel = () => {
    if (filteredBreaks.length === 0) return;
    const headers = [
      "Employee Name",
      "Employee ID",
      "Department",
      "Team Lead",
      "Shift",
      "Break Type",
      "Allowed (min)",
      "Actual (min)",
      "Extra (min)",
      "Date",
      "Status"
    ];
    const rows = filteredBreaks.map((b) => [
      b.employeeName || "",
      b.employeeId || "",
      b.department || "",
      b.leadName || "",
      b.shiftName || "",
      b.breakType || "",
      b.allowedMins || 0,
      b.actualMins || 0,
      b.extraMins || 0,
      b.date || "",
      b.status || ""
    ]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Extra Breaks");
    XLSX.writeFile(wb, `Extra_Break_Report_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const handleExportPDF = () => {
    if (filteredBreaks.length === 0) return;
    const doc = new jsPDF("p", "pt", "a4");
    doc.text("Extra Break Policy Violations Report", 40, 40);
    const headers = ["Employee", "Dept", "Lead", "Type", "Allowed", "Actual", "Extra", "Date"];
    const rows = filteredBreaks.map((b) => [
      b.employeeName || "",
      b.department || "",
      b.leadName || "",
      b.breakType || "",
      `${b.allowedMins}m`,
      `${b.actualMins}m`,
      `${b.extraMins}m`,
      b.date || ""
    ]);

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 60,
    });
    doc.save(`Extra_Break_Report_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  const loading = breaksLoading || usersLoading;

  return (
    <Layout navItems={ADMIN_NAV} panelLabel="Admin Panel">
      <div className="page-header">
        <div>
          <h1 className="page-title">Extra Break Report</h1>
          <p className="page-subtitle">Monitor and review employees exceeding allowed break limits.</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button onClick={handleExportExcel} disabled={filteredBreaks.length === 0} className="btn-secondary">
            Export Excel
          </button>
          <button onClick={handleExportPDF} disabled={filteredBreaks.length === 0} className="btn-secondary">
            Export PDF
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="stats-grid" style={{ marginBottom: "1.5rem" }}>
        <div className="stat-card">
          <div className="stat-label">Total Violations</div>
          <div className="stat-value text-purple">{stats.totalIncidents}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Extra Time</div>
          <div className="stat-value text-red">{stats.totalMins} mins</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Overstay</div>
          <div className="stat-value text-amber">{stats.avgMins} mins</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Top Violator</div>
          <div className="stat-value text-blue" style={{ fontSize: "1.25rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {stats.topViolator} {stats.topCount > 0 ? `(${stats.topCount}x)` : ""}
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="card filters-card" style={{ padding: "1.25rem", marginBottom: "1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1rem" }}>
          <div>
            <label className="form-label" style={{ fontSize: "0.75rem" }}>Search Employee</label>
            <input
              type="text"
              placeholder="Name or ID"
              className="form-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div>
            <label className="form-label" style={{ fontSize: "0.75rem" }}>Department</label>
            <select className="form-select" value={filterDept} onChange={(e) => setFilterDept(e.target.value)}>
              <option value="all">All Departments</option>
              {depts.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label" style={{ fontSize: "0.75rem" }}>Team Lead</label>
            <select className="form-select" value={filterLead} onChange={(e) => setFilterLead(e.target.value)}>
              <option value="all">All Leads</option>
              {leads.map((l) => (
                <option key={l.uid} value={l.uid}>{l.displayName}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label" style={{ fontSize: "0.75rem" }}>Break Type</label>
            <select className="form-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="all">All Types</option>
              <option value="tea1">Tea Break 1</option>
              <option value="tea2">Tea Break 2</option>
              <option value="lunch">Lunch</option>
            </select>
          </div>

          <div>
            <label className="form-label" style={{ fontSize: "0.75rem" }}>Start Date</label>
            <input
              type="date"
              className="form-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div>
            <label className="form-label" style={{ fontSize: "0.75rem" }}>End Date</label>
            <input
              type="date"
              className="form-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card-loading">Loading report data…</div>
      ) : filteredBreaks.length === 0 ? (
        <div className="card-empty">No break policy violations matching your filters.</div>
      ) : (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Dept</th>
                <th>Team Lead</th>
                <th>Shift</th>
                <th>Type</th>
                <th>Duration (Allowed / Actual)</th>
                <th>Extra Time</th>
                <th>Date & Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredBreaks.map((b) => (
                <tr key={b.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{b.employeeName}</div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>ID: {b.employeeId}</div>
                  </td>
                  <td>{b.department || "—"}</td>
                  <td>{b.leadName || "—"}</td>
                  <td>{b.shiftName || "—"}</td>
                  <td style={{ textTransform: "capitalize" }}>{b.breakType || "—"}</td>
                  <td>
                    {b.allowedMins}m / {b.actualMins}m
                  </td>
                  <td style={{ fontWeight: 600, color: "var(--accent-red)" }}>
                    +{b.extraMins}m
                  </td>
                  <td>
                    <div>{b.date}</div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                      {formatTime(b.startTime)} - {formatTime(b.endTime)}
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-error">{b.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}
